import clsx from 'clsx';
import { RefObject } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import HabitatLiveStreamPlayer from '@/components/HabitatLiveStreamPlayer';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import { useLiveStreamPlayerStateContext } from '@/components/LiveStreamPlayer/contexts';
import LiveStreamPlayerLoadingContent from '@/components/LiveStreamPlayer/LiveStreamPlayerLoadingContent';

import AmbientOnboardingModal from '../AmbientOnboardingModal';
import HomeLiveStreamPlayerDesktopControls from './HomeLiveStreamPlayerDesktopControls';
import HomeLiveStreamPlayerMobileControls from './HomeLiveStreamPlayerMobileControls';

interface HomeLiveStreamPlayerOverlayProps {
  miniMode?: boolean;
  preferLiveStreamMutedRef: RefObject<boolean>;
  containerRef: RefObject<HTMLDivElement>;
  isAmbientMode?: boolean;
  onAmbientModeChange?: (isAmbientMode: boolean) => void;
}

const HomeLiveStreamPlayerOverlay = ({
  containerRef,
  miniMode,
  preferLiveStreamMutedRef,
  isAmbientMode,
  onAmbientModeChange,
}: HomeLiveStreamPlayerOverlayProps) => {
  const isMobileLayout = useIsMobileLayout();

  const {
    isPlaying,
    isVideoLoaded,
    volume: liveStreamVolume,
    isMuted: isLiveStreamMuted,
    requestChangePlayingState,
    requestChangeMuteState,
    requestChangeVolume,
  } = useLiveStreamPlayerStateContext();

  const [isMuted, setIsMuted] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(liveStreamVolume);
  const volumeRef = useRef(currentVolume);

  const onMutedChange = (newValue: boolean) => {
    if (!isAmbientMode) {
      requestChangeMuteState(newValue);
    }
    // eslint-disable-next-line no-param-reassign
    preferLiveStreamMutedRef.current = newValue;
    setIsMuted(newValue);
  };

  const onVolumeChange = (newValue: number) => {
    volumeRef.current = newValue;
    if (!isAmbientMode) {
      requestChangeVolume(newValue);
    }
    setCurrentVolume(newValue);
    const isMuted = newValue === 0;
    // eslint-disable-next-line no-param-reassign
    preferLiveStreamMutedRef.current = isMuted;
    setIsMuted(isMuted);
  };

  useEffect(() => {
    if (!isVideoLoaded) return;
    if (miniMode) {
      requestChangeMuteState(true);
    } else {
      requestChangeMuteState(!!preferLiveStreamMutedRef.current);
    }
  }, [miniMode, requestChangeMuteState, preferLiveStreamMutedRef, isVideoLoaded]);

  useEffect(() => {
    if (miniMode || !isVideoLoaded) {
      return;
    }
    if (isAmbientMode) {
      requestChangeMuteState(true);
      // reset the muted state when user exit the ambient mode
      setIsMuted(false);
    } else if (!preferLiveStreamMutedRef.current) {
      requestChangeVolume(volumeRef.current);
    }
  }, [isAmbientMode, requestChangeMuteState, requestChangeVolume, preferLiveStreamMutedRef, isVideoLoaded, miniMode]);

  if (!isVideoLoaded && miniMode) {
    return <p className="preflight preflight-p">Loading...</p>;
  }

  if (!isPlaying && isVideoLoaded) {
    return (
      <HabitatLiveStreamPlayer.FallbackContent>
        {miniMode ? (
          <p className="preflight preflight-p">Paused</p>
        ) : (
          <HabitatLiveStreamPlayer.PauseContent onClick={() => requestChangePlayingState(true)} />
        )}
      </HabitatLiveStreamPlayer.FallbackContent>
    );
  }

  return (
    <>
      {!isVideoLoaded && <LiveStreamPlayerLoadingContent />}

      {/* Live tag */}
      {isPlaying && (
        <div
          className={clsx(
            'absolute flex items-center gap-1 rounded bg-olive-4',
            miniMode ? 'right-2 top-2 px-1.5 py-1' : 'right-5 top-3 px-1.5 py-1 desktop:px-2',
          )}
        >
          <span className="size-1 rounded-full bg-system-red desktop:size-1.5" />
          <span className={clsx('font-bold leading-none', miniMode ? 'text-[8px]' : 'text-[10px] desktop:text-xs')}>
            LIVE
          </span>
        </div>
      )}

      {/* Controls */}
      {isMobileLayout ? (
        isPlaying && <HomeLiveStreamPlayerMobileControls containerRef={containerRef} onMutedChange={onMutedChange} />
      ) : (
        <HomeLiveStreamPlayerDesktopControls
          containerRef={containerRef}
          miniMode={miniMode}
          isAmbientMode={isAmbientMode}
          isMuted={isAmbientMode ? isMuted : isLiveStreamMuted}
          onMutedChange={onMutedChange}
          volume={isAmbientMode ? currentVolume : liveStreamVolume}
          onVolumeChange={onVolumeChange}
          onAmbientModeChange={onAmbientModeChange}
        />
      )}

      {isAmbientMode && <AmbientOnboardingModal />}
    </>
  );
};

export default HomeLiveStreamPlayerOverlay;
